import { useRef } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import type { PageExperience } from '@/features/experiences/types';
import {
  rendersHeaderAttribution,
  rendersHeaderDescription,
  supportsAttribution,
  supportsBanner,
  supportsLogo,
} from '@/features/layouts/layout.lib';
import {
  AttributionOptions,
  BannerOptions,
  BlockLayouts,
  BlockTypes,
  HeaderTitleSizes,
  LogoOptions,
} from '@/types/gql.generated';
import { Box, Flex, Icon, Text } from '@/ui';
import {
  SidebarCard,
  SidebarIconChip,
  SidebarRow,
  SidebarRowLabel,
  SidebarSection,
} from '../SidebarKit';
import { SidebarRowSwitch } from '../SidebarRowSwitch';
import type { SegmentedOption } from '../StylePanel/SegmentedControl';
import { SegmentedControl } from '../StylePanel/SegmentedControl';
import { useCustomizeControl } from '../useCustomizeControl';
import { LogoImageRow } from './LogoImageRow';
import { TitleInput } from './TitleInput';

// The three "A" glyphs grow with the size so the control previews its effect;
// value maps 1:1 to the server headerTitleSize enum.
const TITLE_SIZE_OPTIONS: SegmentedOption<HeaderTitleSizes>[] = [
  {
    value: HeaderTitleSizes.Small,
    ariaLabel: 'Small title',
    label: (
      <Text as="span" fontFamily="display" fontSize="13px" lineHeight={1}>
        A
      </Text>
    ),
  },
  {
    value: HeaderTitleSizes.Medium,
    ariaLabel: 'Medium title',
    label: (
      <Text as="span" fontFamily="display" fontSize="16px" lineHeight={1}>
        A
      </Text>
    ),
  },
  {
    value: HeaderTitleSizes.Large,
    ariaLabel: 'Large title',
    label: (
      <Text as="span" fontFamily="display" fontSize="20px" lineHeight={1}>
        A
      </Text>
    ),
  },
];

type Props = {
  experience: PageExperience;
  onSetTitle: (title: string) => void;
  onSetBlockLayout: (blockId: string, layout: BlockLayouts) => void;
};

export const HeaderSection = ({
  experience,
  onSetTitle,
  onSetBlockLayout,
}: Props) => {
  const { register, registerToggle, savedFor } = useCustomizeControl();

  // This field is the title the view's header renders, and nothing else — it does
  // not name the view, which the pickers derive from its layout (see
  // lib/viewName). Left empty, the header falls back to the owning planner's
  // title, which is the placeholder here so the default is visible without being
  // persisted (renaming the planner keeps flowing through).
  const { parentExperience } = usePageExperienceContext();

  // Mirrors what the toggles commit (see toggleBannerDisplay/toggleLogoDisplay):
  // showing one picks the layout's first visible option, hiding it is None.
  const visibleBannerOption = experience.layout.bannerOptions.find(
    (item) => item !== BannerOptions.None
  );

  // Offered only where the server lists None among the header block's options:
  // getBlockUpdates validates against them and rejects anything else.
  const headerBlock = experience.blocks.find(
    (item) => item.type === BlockTypes.Header
  );
  const headerBlockOptions =
    experience.layout.blocks.find((item) => item.type === BlockTypes.Header)
      ?.layouts ?? [];
  // A missing block counts as no header, not a shown one: Header renders nothing
  // without one, so its contents' switches would write columns nobody can see.
  const isHeaderShown =
    headerBlock !== undefined && headerBlock.layoutId !== BlockLayouts.None;

  // The block's layoutId is both "which header" and "whether a header", so
  // switching off overwrites the chosen style. Remember it, or a view on the
  // layout's second header option (the List layout offers a wide variant) came
  // back as the first one — a different header, byline and all.
  //
  // Per view, not per mount: switching views keeps the previous data (and so
  // this component) alive, and one shared slot handed the next view whatever
  // the last one remembered — a header its layout may not even offer, which the
  // server rejects. The offers check is the second half of that guard, for a
  // view whose layout changed under the same id.
  const lastShownHeader = useRef<Record<string, BlockLayouts>>({});
  if (headerBlock && isHeaderShown) {
    lastShownHeader.current[experience.id] = headerBlock.layoutId;
  }
  const remembered = lastShownHeader.current[experience.id];
  const restoredHeaderOption =
    (remembered && headerBlockOptions.includes(remembered)
      ? remembered
      : undefined) ??
    headerBlockOptions.find((item) => item !== BlockLayouts.None);

  const canHideHeader =
    Boolean(headerBlock) &&
    headerBlockOptions.includes(BlockLayouts.None) &&
    Boolean(restoredHeaderOption);

  // The upload row rides on the switch below rather than binding a field of its
  // own, so it reads the saved value from the same copy that switch does.
  const showLogo = isHeaderShown && supportsLogo(experience.layout);
  const isLogoShown =
    showLogo && savedFor('child').logoDisplay !== LogoOptions.None;

  const showAttribution =
    isHeaderShown &&
    supportsAttribution(experience.layout) &&
    rendersHeaderAttribution(headerBlock?.layoutId);
  const showDescription =
    isHeaderShown &&
    experience.layout.supportsDescription &&
    rendersHeaderDescription(headerBlock?.layoutId);

  return (
    <SidebarSection label="Header">
      <TitleInput
        placeholder={parentExperience?.title}
        value={experience.title ?? ''}
        onSave={onSetTitle}
      />
      <SidebarCard>
        {canHideHeader && headerBlock && restoredHeaderOption && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Layout" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel
                label="Show header"
                sublabel="The whole section above your events"
              />
            </Flex>
            <SidebarRowSwitch
              aria-label="Show header"
              flexShrink={0}
              isChecked={isHeaderShown}
              ml={2}
              onChange={(event) =>
                onSetBlockLayout(
                  headerBlock.id,
                  event.target.checked
                    ? restoredHeaderOption
                    : BlockLayouts.None
                )
              }
            />
          </SidebarRow>
        )}
        {isHeaderShown && supportsBanner(experience.layout) && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Image" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel
                label="Page banner"
                sublabel="A header image across the top"
              />
            </Flex>
            <SidebarRowSwitch
              aria-label="Page banner"
              flexShrink={0}
              ml={2}
              {...registerToggle('child', 'bannerDisplay', {
                toChecked: (display) => display !== BannerOptions.None,
                toField: (shown) =>
                  shown
                    ? (visibleBannerOption ?? BannerOptions.None)
                    : BannerOptions.None,
              })}
            />
          </SidebarRow>
        )}
        {isHeaderShown && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="LetterText" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Title" />
            </Flex>
            <SidebarRowSwitch
              aria-label="Title"
              flexShrink={0}
              ml={2}
              {...registerToggle('child', 'headerTitleEnabled')}
            />
          </SidebarRow>
        )}

        {showDescription && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="List" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel
                label="Description"
                sublabel="Your intro text under the title"
              />
            </Flex>
            <SidebarRowSwitch
              aria-label="Description"
              flexShrink={0}
              ml={2}
              {...registerToggle('child', 'descriptionEnabled')}
            />
          </SidebarRow>
        )}

        {showAttribution && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="User" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel
                label="Author"
                sublabel="Your name under the title"
              />
            </Flex>
            <SidebarRowSwitch
              aria-label="Author"
              flexShrink={0}
              ml={2}
              {...registerToggle('child', 'attributionDisplay', {
                toChecked: (display) => display !== AttributionOptions.None,
                toField: (shown) =>
                  shown
                    ? AttributionOptions.AvatarFullName
                    : AttributionOptions.None,
              })}
            />
          </SidebarRow>
        )}

        {showLogo && (
          <SidebarRow>
            <Flex align="center" gap={2.5} minW={0}>
              <SidebarIconChip size="26px">
                <Icon color="ink.soft" icon="Hexagon" size={3.5} />
              </SidebarIconChip>
              <SidebarRowLabel label="Logo" />
            </Flex>
            <SidebarRowSwitch
              aria-label="Logo"
              flexShrink={0}
              ml={2}
              {...registerToggle('child', 'logoDisplay', {
                toChecked: (display) => display !== LogoOptions.None,
                toField: (shown) =>
                  shown ? LogoOptions.Square : LogoOptions.None,
              })}
            />
          </SidebarRow>
        )}

        {/* Only once the mark is on: switched off, the header draws nothing to
            upload for, and the switch is the way back. */}
        {isLogoShown && (
          <LogoImageRow isSharedAcrossViews={parentExperience !== undefined} />
        )}

        {isHeaderShown && (
          <SidebarRow>
            <SidebarRowLabel label="Title size" />
            <Box flexShrink={0} w="150px">
              <SegmentedControl
                options={TITLE_SIZE_OPTIONS}
                {...register('child', 'headerTitleSize')}
              />
            </Box>
          </SidebarRow>
        )}
      </SidebarCard>
    </SidebarSection>
  );
};
